import { useEffect, useRef } from 'react';
import _ from 'lodash';
import { markLastActivity, getLastActivity, clearLastActivity } from '@/shared/helpers/lastActivity';

const ACTIVITY_EVENTS = ['click', 'keydown', 'scroll', 'mousemove'] as const;
const UPDATE_DEBOUNCE_MS = 1000;
const CHECK_INTERVAL_MS = 1000;

// tracks click/keydown/scroll/mousemove, debouncing writes of the last-activity timestamp to
// sessionStorage (seeded on login via setToken), and fires onInactive once timeoutMs has
// elapsed since that timestamp. Only runs while `enabled` (i.e. actually logged in).
export const useActivityMonitor = (onInactive: () => void, timeoutMs: number, enabled: boolean) => {
    const onInactiveRef = useRef(onInactive);
    onInactiveRef.current = onInactive;

    useEffect(() => {
        if (!enabled) {
            clearLastActivity();
            return;
        }

        const debouncedMarkActivity = _.debounce(markLastActivity, UPDATE_DEBOUNCE_MS);

        const intervalId = setInterval(() => {
            const lastActivity = getLastActivity();
            if (!lastActivity || Date.now() - lastActivity > timeoutMs) {
                clearLastActivity();
                onInactiveRef.current();
            }
        }, CHECK_INTERVAL_MS);

        ACTIVITY_EVENTS.forEach(event => window.addEventListener(event, debouncedMarkActivity, { passive: true }));

        return () => {
            debouncedMarkActivity.cancel();
            clearInterval(intervalId);
            ACTIVITY_EVENTS.forEach(event => window.removeEventListener(event, debouncedMarkActivity));
        };
    }, [enabled, timeoutMs]);
};

export default useActivityMonitor;
